import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React from 'react';
import {Handle, Position, ReactFlowInstance} from 'reactflow';

import {AddNodeDropdown} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/AddNodeDropdown';
import {
  HANDLE_TYPE_FIELD,
  HANDLE_DIRECTION_SOURCE,
  HANDLE_DIRECTION_TARGET,
  HandleDirection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {GetAddNodeOptionsForRightHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import AddNodeHandle, {AddNodeHandleProps} from 'ui-library/reactflow/handles/AddNodeHandle';
import {ReactFlowLockedContext} from 'ui-library/reactflow/LockedContext';
import {
  NODE_HEADER_HANDLE_STYLES,
  NodeHeaderEdgeHandleStyle,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';

import {FlowchartEditorModeContext} from '../FlowchartEditorModeContext';
import {HandlesContext, HandlesContextValue} from '../HandlesContext';
import {FlowchartEditorMode} from '../ProcessFlowchartEditorModeConstants';

function isDraggingFromFieldHandle(connectionDragState: HandlesContextValue['connectionDragState']): boolean {
  return (
    connectionDragState?.isDragging === true &&
    connectionDragState.sourceHandleInfo?.handleCategory === HANDLE_TYPE_FIELD
  );
}

/**
 * Context-aware handle component that only re-renders when drag state changes,
 * without causing parent node to re-render
 */
function ContextAwareNodeHandleComponent({
  id,
  type,
  position,
  handleStyle,
  testProps,
}: {
  id: string;
  type: HandleDirection;
  position: Position;
  handleStyle: NodeHeaderEdgeHandleStyle;
  testProps: ReturnType<typeof getTestProps>;
}): JSX.Element | null {
  const handlesContext = HandlesContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const hideForViewer = locked || editorMode === FlowchartEditorMode.EXECUTION_VIEWER;

  if (isDraggingFromFieldHandle(handlesContext.connectionDragState)) {
    return null;
  }

  return (
    <Handle
      id={id}
      type={type}
      position={position}
      className={css(
        NODE_HEADER_HANDLE_STYLES.nodeHandle,
        handleStyle,
        hideForViewer && NODE_HEADER_HANDLE_STYLES.lockedNodeHandle
      )}
      {...testProps}
    >
      <div
        className={css(
          NODE_HEADER_HANDLE_STYLES.handleDot,
          hideForViewer && NODE_HEADER_HANDLE_STYLES.hidden
        )}
      />
    </Handle>
  );
}

const ContextAwareNodeHandle = React.memo(ContextAwareNodeHandleComponent);

/**
 * Context-aware AddNodeHandle that hides when dragging from field handle
 */
function ContextAwareAddNodeHandleComponent(props: {
  id: string;
  type: typeof HANDLE_DIRECTION_SOURCE;
  isConnectable: boolean;
  disabled?: boolean;
  renderPopoverContent?: () => JSX.Element;
  onClick?: () => void;
}): JSX.Element | null {
  const handlesContext = HandlesContext.useContext();
  const editorMode = FlowchartEditorModeContext.useContext();
  const locked = ReactFlowLockedContext.useContext() ?? false;
  const hideForViewer = locked || editorMode === FlowchartEditorMode.EXECUTION_VIEWER;

  if (isDraggingFromFieldHandle(handlesContext.connectionDragState)) {
    return null;
  }

  // Use CSS to hide when locked or hidden for viewer (keeps handle in DOM for ReactFlow layout)
  if (hideForViewer) {
    return (
      <span style={{visibility: 'hidden', pointerEvents: 'none'}}>
        <AddNodeHandle {...props} />
      </span>
    );
  }

  return <AddNodeHandle {...props} />;
}

const ContextAwareAddNodeHandle = React.memo(ContextAwareAddNodeHandleComponent);

interface CreateHeaderPropsForNodeOptions {
  hasLeftHandle?: boolean;
  hasRightHandle?: boolean;
  isDisabled?: boolean;
  isExecutionViewer?: boolean;
}

export default function createHeaderPropsForNode(
  nodeData: {
    id: string;
    nodeName: string;
    nodeIcon?: BenchlingIconName;
    nodeSecondaryInfo?: string;
    nodeType: ProcessFlowchartNodeType;
    position: {x: number; y: number};
    getAddNodeOptionsForRightHandle?: GetAddNodeOptionsForRightHandle;
  },
  reactFlowInstance: ReactFlowInstance,
  {
    hasLeftHandle = true,
    hasRightHandle = true,
    isDisabled = false,
    isExecutionViewer = false,
  }: CreateHeaderPropsForNodeOptions = {}
): {
  title: string;
  subTitle?: string;
  leftIcon?: BenchlingIconName;
  leftHandle?: React.ReactNode;
  rightHandle?: React.ReactNode;
} {
  const {id, nodeName, nodeIcon, nodeSecondaryInfo} = nodeData;
  const addNodesOptions = nodeData.getAddNodeOptionsForRightHandle?.(nodeData.id, reactFlowInstance);
  const addNodeHandleProps: Pick<AddNodeHandleProps, 'renderPopoverContent' | 'onClick' | 'disabled'> = {
    disabled: isDisabled,
  };
  if (addNodesOptions != null) {
    // If there is exactly one option, we perform that action on click instead of showing a popover menu.
    if (addNodesOptions.length === 1) {
      addNodeHandleProps.onClick = addNodesOptions[0].onSelect;
    } else if (addNodesOptions.length > 1) {
      addNodeHandleProps.renderPopoverContent = () => <AddNodeDropdown options={addNodesOptions} />;
    }
  }

  const targetHandlePosition = isExecutionViewer ? Position.Top : Position.Left;
  const sourceHandlePosition = isExecutionViewer ? Position.Bottom : Position.Right;
  const targetHandleStyle = isExecutionViewer
    ? NODE_HEADER_HANDLE_STYLES.topHandle
    : NODE_HEADER_HANDLE_STYLES.leftHandle;
  const sourceHandleStyle = isExecutionViewer
    ? NODE_HEADER_HANDLE_STYLES.bottomHandle
    : NODE_HEADER_HANDLE_STYLES.rightHandle;

  return {
    title: nodeName,
    subTitle: nodeSecondaryInfo,
    leftIcon: nodeIcon,
    leftHandle: hasLeftHandle ? (
      <ContextAwareNodeHandle
        id={defaultNodeHandleIdGenerator(id, HANDLE_DIRECTION_TARGET)}
        type={HANDLE_DIRECTION_TARGET}
        position={targetHandlePosition}
        handleStyle={targetHandleStyle}
        testProps={getTestProps({
          component: 'ProcessFlowchartNode',
          element: 'targetHandle',
          key: nodeName,
        })}
      />
    ) : null,
    rightHandle: hasRightHandle ? (
      isExecutionViewer ? (
        <ContextAwareNodeHandle
          id={defaultNodeHandleIdGenerator(id, HANDLE_DIRECTION_SOURCE)}
          type={HANDLE_DIRECTION_SOURCE}
          position={sourceHandlePosition}
          handleStyle={sourceHandleStyle}
          testProps={getTestProps({
            component: 'ProcessFlowchartNode',
            element: 'sourceHandle',
            key: nodeName,
          })}
        />
      ) : (
        <ContextAwareAddNodeHandle
          id={defaultNodeHandleIdGenerator(id, HANDLE_DIRECTION_SOURCE)}
          type={HANDLE_DIRECTION_SOURCE}
          isConnectable={!isDisabled}
          {...addNodeHandleProps}
        />
      )
    ) : null,
  };
}
